<template>
  <b-card
    class="live-map-vessel-details"
    :class="expanded ? 'expanded' : 'collapsed'"
    no-body
  >
    <vue-perfect-scrollbar v-if="expanded" class="h-100">
      <b-card-body class="h-100">
        <h4 @click="toggleExpanded" class="collapse-link mt-1">
          Vessel details
          <i
            title="Collapse details"
            class="fa fa-lg fa-angle-double-right"
          ></i>
        </h4>

        <div v-if="vessel.imo">
          <div
            v-for="(itemTitle, index) in Object.keys(vesselBasicDetails)"
            :key="`details-${index}`"
            class="details-row"
          >
            <div>{{ itemTitle }}:</div>
            <div>{{ vesselBasicDetails[itemTitle] }}</div>
          </div>

          <CTabs class="mt-4" :active-tab.sync="activeTab">
            <CTab title="Voyages" active>
              <c-table
                class="voyages-table"
                no-padding
                :table-data="voyages.items"
                :fields="voyages.columns"
                :striped="true"
                :bordered="false"
                :small="false"
                :perPage="20"
                caption="Last voyages"
              >
              </c-table>
            </CTab>

            <CTab title="Port Calls" active>
              <c-table
                class="voyages-table"
                no-padding
                :table-data="portCalls.items"
                :fields="portCalls.columns"
                :striped="true"
                :bordered="false"
                :small="false"
                :perPage="20"
                caption="Last Port Calls"
              >
              </c-table>
            </CTab>

            <CTab title="Raw Events" active>
              <c-table
                class="voyages-table"
                no-padding
                :table-data="rawEvents.items"
                :fields="rawEvents.columns"
                :striped="true"
                :bordered="false"
                :small="false"
                :perPage="20"
                caption="Raw Events"
              >
              </c-table>
            </CTab>

            <CTab title="Info" active>
              <div
                v-for="(itemTitle, index) in Object.keys(vesselExtraDetails)"
                :key="`details-${index}`"
                class="details-row"
              >
                <div>{{ itemTitle }}:</div>
                <div>{{ vesselExtraDetails[itemTitle] }}</div>
              </div>
            </CTab>
          </CTabs>
        </div>

        <div v-else class="empty-result">
          <div>
            Please select a vessel on the map.
          </div>
        </div>
      </b-card-body>
    </vue-perfect-scrollbar>

    <div v-else class="collapsed-details">
      <div
        @click="toggleExpanded"
        title="Expand details"
        class="collapsed-details__header"
      >
        <i class="fa fa-lg fa-angle-double-left expand-icon"></i>
        Vessel details
      </div>
    </div>
  </b-card>
</template>

<script>
import getNested from "lodash-es/get";
import startCase from "lodash-es/startCase";
import camelCase from "lodash-es/camelCase";
import moment from "moment";
import { mapActions } from "vuex";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import cTable from "../Table";
import { formatVolume } from "@/shared/number-utils";
import { getVesselData } from "@/shared/api_formatters/vessels";
import isEmpty from "lodash-es/isEmpty";

export default {
  name: "live-map-vessel-details",

  props: {
    vessel: {
      type: Object,
      default: () => ({})
    },

    expanded: {
      type: Boolean,
      default: false
    }
  },

  components: {
    // Voyages,
    VuePerfectScrollbar,
    "c-table": cTable
  },

  data() {
    return {
      activeTab: 0,
      currentVesselIMO: null,
      operationMap: {
        L: "Load",
        Lx: "Load",
        Y: "Yard",
        D: "Discharge",
        B: "Bunker",
        Dx: "Discharge",
        U: "Unknown"
      },
      rawEvents: {
        items: [],
        columns: [
          {
            key: "timestamp",
            label: "Timestamp",
            thClass: "white-space-pre",
            tdClass: "white-space-pre"
          },
          {
            key: "eventType",
            label: "Event Type",
            thClass: "white-space-pre",
            tdClass: "white-space-pre"
          },
          {
            key: "eventValue",
            label: "Details",
            thClass: "white-space-pre",
            tdClass: "white-space-pre"
          }
        ]
      },
      portCalls: {
        items: [],
        currentPage: null,
        columns: [
          // { key: 'load_port_name__display', label: 'Load port name' },
          // { key: 'discharge_port_name__display', label: 'Discharge port name' },
          {
            key: "load__display",
            label: "Port Name\nArrival->Departure",
            thClass: "white-space-pre",
            tdClass: "white-space-pre"
          },
          {
            key: "discharge__display",
            label: "Operation\nDays In Port",
            thClass: "white-space-pre",
            tdClass: "white-space-pre"
          },
          {
            key: "commodity_volume__display",
            label: "Commodity\nVolume",
            thClass: "white-space-pre",
            tdClass: "white-space-pre"
          }
          // 'period',
          // 'commodity',
          // 'volume',
        ]
      },
      voyages: {
        items: [],
        currentPage: null,
        columns: [
          // { key: 'load_port_name__display', label: 'Load port name' },
          // { key: 'discharge_port_name__display', label: 'Discharge port name' },
          {
            key: "load__display",
            label: "Load Port\nLoad Date",
            thClass: "white-space-pre",
            tdClass: "white-space-pre"
          },
          {
            key: "discharge__display",
            label: "Discharge Port\nDischarge Date",
            thClass: "white-space-pre",
            tdClass: "white-space-pre"
          },
          {
            key: "commodity_volume__display",
            label: "Commodity\nVolume",
            thClass: "white-space-pre",
            tdClass: "white-space-pre"
          }
          // 'period',
          // 'commodity',
          // 'volume',
        ]
      }
    };
  },

  methods: {
    getNested: getNested,

    ...mapActions("api/tradeflows", ["getVoyages", "getPortCalls"]),

    ...mapActions("api/vessels", ["getRawVesselEvents"]),

    async loadVoyages() {
      this.voyages.items = [];
      this.voyages.currentPage = 1;

      let response = (await this.getVoyages(this.filterParameters)) || {};
      if (response.data && response.data.data) {
        this.voyages.items = response.data.data.map(item => {
          // 'load_berth_arrival',
          // 'load_berth_departure',
          const departureDate = this.formatPeriodDate(item.loadPortDepartedAt);
          const arrivalDate = this.formatPeriodDate(
            item.dischargePortDepartedAt
          );
          let commodity = startCase(camelCase(item.commodity));
          commodity = commodity === "Unknown Commodity" ? "Unknown" : commodity;
          const volume = formatVolume(item.volume);

          return {
            ...item,
            // load_port_name__display: `${item.load_port_name} (${item.load_country_code})`,
            // discharge_port_name__display: `${item.discharge_port_name} (${item.discharge_country_code})`,
            volume: volume,
            load__display:
              `${item.loadPortName} (${item.loadCountryCode})\n` +
              `${departureDate}`,
            discharge__display: `${item.dischargePortName} (${item.dischargeCountryCode})\n${arrivalDate}`,
            commodity_volume__display: `${commodity}\n${volume}`
          };
        });
      }
    },

    async loadPortCalls() {
      this.portCalls.items = [];
      this.portCalls.currentPage = 1;

      let response = (await this.getPortCalls(this.filterParameters)) || {};
      if (response.data && response.data.data) {
        this.portCalls.items = response.data.data.map(item => {
          // 'load_berth_arrival',
          // 'load_berth_departure',
          const arrivalDate = this.formatPeriodDateHour(item.arrivedAt);
          const departureDate = this.formatPeriodDateHour(item.departedAt);
          let commodity = startCase(camelCase(item.commodity));
          commodity = commodity === "Unknown Commodity" ? "Unknown" : commodity;
          let volume;
          if (item.volume) {
            volume = formatVolume(item.volume) + " (" + item.operation + ")";
          } else {
            volume = "-";
          }

          return {
            ...item,
            // load_port_name__display: `${item.load_port_name} (${item.load_country_code})`,
            // discharge_port_name__display: `${item.discharge_port_name} (${item.discharge_country_code})`,
            volume: volume,
            load__display:
              `${item.portName} (${item.countryCode})\n` +
              `${arrivalDate} --> ${departureDate}`,
            discharge__display: `${item.operation}\n${item.daysInPort} days`,
            commodity_volume__display: `${commodity}\n${volume}`
          };
        });
      }
    },

    async loadRawVesselEvents() {
      this.rawEvents.items = [];

      let response = await this.getRawVesselEvents(this.filterParameters);
      this.rawEvents.items = response.reverse();
    },

    toggleExpanded() {
      this.$emit("update:expanded", !this.expanded);
    },

    getDestination(vessel) {
      const portName = getNested(vessel, "state.destination.portName", null);
      const countryCode = getNested(vessel, "state.destination.country", null);
      const unlocode = getNested(vessel, "state.destination.unlocode", null);
      const region = getNested(vessel, "state.destination.region", null);

      if (portName && unlocode) {
        return `${portName} (${unlocode}) || ${region}`;
      }

      if (portName && countryCode) {
        return `${portName} (${countryCode}) || ${region}`;
      }

      const rawDestination = getNested(
        vessel,
        "state.destination.rawDestination",
        null
      );
      if (rawDestination) {
        return rawDestination + " (unparsed)";
      }

      const staticDestination = getNested(vessel, "static.destination", "--");
      return `${staticDestination} (unparsed)`;
    },

    formatDate(dateString) {
      if (dateString) {
        return moment(dateString).format("YYYY-MM-DD HH:mm:ss");
      } else {
        return "--";
      }
    },

    formatPeriodDate(dateString) {
      if (dateString) {
        return moment(dateString)
          .format("DD-MMM YY")
          .toUpperCase(); // 17-FEB 17
      } else {
        return "N/A";
      }
    },
    formatPeriodDateHour(dateString) {
      if (dateString) {
        return moment(dateString)
          .format("DD-MMM YY  HH:mm")
          .toUpperCase(); // 17-FEB 17
      } else {
        return "N/A";
      }
    }
  },

  computed: {
    filterParameters() {
      return {
        imo: [this.vessel.imo]
      };
    },

    vesselData() {
      return getVesselData(this.vessel);
    },

    vesselBasicDetails() {
      return {
        IMO: this.vesselData.imo,
        "Vessel name": this.vesselData.vesselName,
        DWT: formatVolume(this.vesselData.dwt),
        Segment: this.vesselData.segment,
        "Laden status": this.vesselData.ladenStatus
        // 'Laden percent': this.vesselData.ladenPercent,
      };
    },

    vesselExtraDetails() {
      return {
        Destination: this.getDestination(this.vessel),
        "Last update": this.vesselData.lastUpdate,
        ETA: this.vesselData.eta,
        "Origin port": "--",
        Volume: "--",
        Commodity: "--",
        "Days since departure": "--",
        "Days until arrival": "--",
        Draught: this.vesselData.draught
      };
    }
  },

  watch: {
    async expanded(value) {
      if (value) {
        this.currentVesselIMO = getNested(this.vessel, "imo");
        await this.loadVoyages();
        await this.loadRawVesselEvents();
        await this.loadPortCalls();
      }
    },

    async vessel() {
      if (this.expanded && !isEmpty(this.vessel)) {
        if (this.currentVesselIMO !== getNested(this.vessel, "imo")) {
          // prevent voyages double loading when map follows a vessel popup and refreshes data
          this.currentVesselIMO = getNested(this.vessel, "imo");
          await this.loadVoyages();
          await this.loadRawVesselEvents();
          await this.loadPortCalls();
        }
      }
    }
  }
};
</script>

<style scoped>
.voyages-table >>> .white-space-pre {
  white-space: pre !important;
}
</style>
